// POC blush renderer for the photo-based VTO pipeline.
//
// Renders a radial gradient at each cheek apex with `multiply` blend, so
// the underlying skin texture survives the tint. Apex position is estimated
// from MediaPipe FaceMesh landmarks (50 / 280 are the canonical cheek-apex
// seeds); radius scales to the detected face width to stay proportional on
// close-ups and pulled-back shots.
//
// This is v0.1 POC — no finish-awareness, no pose-aware tilt correction,
// no tone mapping. Acceptable only on front-facing, well-lit snapshots.
// App.svelte displays an explicit disclaimer to set that expectation.

import type { LipDetection } from '../tracking/faceLandmarker';
import type { SemanticFaceMap } from '../semantic/semanticFaceMap';
import { combineMasks, intersectMasks, scaleMask } from './maskUtils';
import { stackBlur } from './stackBlur';

export interface CheekOptions {
  hex: string;
  finish: string;   // kit-level finish string: cream/matte/satin/sheer/...
  opacity?: number;
  opacityScale?: number;
  radiusScale?: number;
  drapeScale?: number;
  semanticMap?: SemanticFaceMap | null;
}

export interface CheekRenderResult {
  canvas: HTMLCanvasElement;
}

// MediaPipe FaceMesh landmark indices for each cheek apex. 50 and 280 are
// the commonly-cited "cheek apex" seeds in the FaceMesh reference.
const LEFT_CHEEK_APEX  = 50;
const RIGHT_CHEEK_APEX = 280;

// Auxiliary landmarks used to stabilise the apex (blend with the primary
// seed so minor landmark jitter doesn't snap the blush position around).
const LEFT_CHEEK_AUX  = [101, 36];   // lateral nose base + upper cheek
const RIGHT_CHEEK_AUX = [330, 266];
const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
  361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
  162, 21, 54, 103, 67, 109
];

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16)
  ];
}

// Rec. 709 relative luminance in 0..1. Used to route deep/saturated
// burgundy blushes (noir-classique's #8b4050 is the trigger case) off the
// multiply path, where they crush skin highlights into a bruise-looking
// patch, and onto a low-alpha source-over path that reads as powdered
// pigment deposited over the skin instead.
function luminance([r, g, b]: [number, number, number]): number {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function weightedApex(
  detection: LipDetection,
  primaryIdx: number,
  auxIndices: number[]
): { x: number; y: number } | null {
  const { landmarks, imageWidth: W, imageHeight: H } = detection;
  const primary = landmarks[primaryIdx];
  if (!primary) return null;
  let sx = primary.x * W * 2;   // primary weight = 2
  let sy = primary.y * H * 2;
  let weightSum = 2;
  for (const idx of auxIndices) {
    const p = landmarks[idx];
    if (!p) continue;
    sx += p.x * W;
    sy += p.y * H;
    weightSum += 1;
  }
  return { x: sx / weightSum, y: sy / weightSum };
}

// Estimate face width from the two most-lateral face-oval landmarks so
// blush radius stays proportional to face size on different crop levels.
// 234 = left temple, 454 = right temple in FaceMesh.
function estimateFaceWidth(detection: LipDetection): number {
  const { landmarks, imageWidth: W } = detection;
  const left = landmarks[234];
  const right = landmarks[454];
  if (!left || !right) return detection.imageWidth * 0.5;
  return Math.abs((right.x - left.x) * W);
}

function tracePath(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  indices: readonly number[]
): void {
  const { landmarks, imageWidth: W, imageHeight: H } = detection;
  let started = false;
  for (const idx of indices) {
    const p = landmarks[idx];
    if (!p) continue;
    const x = p.x * W;
    const y = p.y * H;
    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  if (started) ctx.closePath();
}

function semanticCheekMatte(
  semanticMap: SemanticFaceMap | null | undefined,
  width: number,
  height: number
): HTMLCanvasElement | null {
  if (!semanticMap) return null;
  const cheeks = combineMasks(width, height, [
    semanticMap.masks.leftCheek,
    semanticMap.masks.rightCheek
  ]);
  return intersectMasks(cheeks, [scaleMask(semanticMap.masks.skin, width, height)]);
}

// Per-finish opacity defaults. v0.3.0 (Session A rebalance wave): bumped
// ~25% from v0.2.2 because co-design feedback on live session showed the
// blush was too subtle to register at first glance next to the lip layer
// — users couldn't tell blush was applied without toggling the Before/After
// comparison. New values keep the powder-diffuse feel (still well below
// the lip's opacity band) while making the cheek visible at a glance.
function defaultOpacityFor(finish: string): number {
  const f = finish.toLowerCase();
  if (f === 'sheer') return 0.17;
  if (f === 'cream') return 0.27;
  if (f === 'matte') return 0.23;
  if (f === 'satin') return 0.25;
  return 0.24;
}

export function renderCheekOverlay(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection,
  options: CheekOptions
): CheekRenderResult {
  const W = detection.imageWidth;
  const H = detection.imageHeight;
  const out = document.createElement('canvas');
  out.width = W;
  out.height = H;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');

  // Layer 0: original snapshot (or the canvas from a previous layer).
  ctx.drawImage(source, 0, 0, W, H);

  const left = weightedApex(detection, LEFT_CHEEK_APEX, LEFT_CHEEK_AUX);
  const right = weightedApex(detection, RIGHT_CHEEK_APEX, RIGHT_CHEEK_AUX);
  if (!left && !right) return { canvas: out };

  const faceWidth = estimateFaceWidth(detection);
  // v0.4.4: widened 17% → 22% of face width. Live feedback reported the
  // blush reading as "concentrated on the cheekbones, not uniform cheek
  // coverage" — real blush drapes across the whole cheek, not just the
  // zygomatic apex. The teardrop offset toward the nose (0.30 below)
  // plus the wider radius now reaches from just below the under-eye
  // down to about mid-cheek, and from the zygomatic inward to the
  // nose-cheek crease — a natural drape, not a circular patch.
  const radius = Math.max(32, faceWidth * 0.215 * (options.radiusScale ?? 1));

  const rgb = hexToRgb(options.hex);
  const [r, g, b] = rgb;
  const baseOpacity = options.opacity ?? defaultOpacityFor(options.finish);
  const baseAlpha = baseOpacity * (options.opacityScale ?? 1);

  // Real blush is pigment sitting over skin, not a pure shadow. Earlier
  // multiply-only passes disappeared on clean, fair real-world photos and
  // left the VTO reading as lipstick-only. Source-over with a bounded peak
  // alpha keeps texture visible while making cheek colour register.
  const useSourceOver = luminance(rgb) < 0.40;
  const alpha = Math.min(baseAlpha, useSourceOver ? 0.20 : 0.30);
  const blendMode = 'source-over';

  // Landmark 4 = nose tip; used as the medial reference point for the
  // nose-ward drape. Falls back to a straight-up-in-face-frame rotation
  // if absent, identical to v0.3.0 Session A behaviour.
  const noseRef = detection.landmarks[4];
  const noseX = noseRef ? noseRef.x * W : null;
  const noseY = noseRef ? noseRef.y * H : null;

  // cos(yaw) drops to ~0.87 at 30° yaw. Clamp at 0.6 so a chance large-yaw
  // misdetection doesn't collapse the gradient to a sliver; anything below
  // ~55° is already out of the "usable" pose.
  const yawScale = Math.max(0.6, Math.cos(detection.pose.yaw));
  const roll = detection.pose.roll;

  const overlay = document.createElement('canvas');
  overlay.width = W;
  overlay.height = H;
  const octx = overlay.getContext('2d');
  if (!octx) return { canvas: out };

  octx.globalCompositeOperation = blendMode;

  for (const apex of [left, right]) {
    if (!apex) continue;

    // Local frame: origin at apex, +X points from apex toward the nose
    // tip. This replaces the v0.3.0 "rotate by roll" with a per-cheek
    // medial-axis rotation, which is naturally pose-aware (both apex and
    // nose landmarks move with head pose) AND gives us a clean direction
    // for the nose-ward drape below. yawScale compresses +X (the medial
    // axis) which matches how the cheek actually foreshortens under yaw.
    const angleToNose =
      noseX !== null && noseY !== null
        ? Math.atan2(noseY - apex.y, noseX - apex.x)
        : roll;

    octx.save();
    octx.translate(apex.x, apex.y);
    if (angleToNose !== 0) octx.rotate(angleToNose);
    if (yawScale !== 1) octx.scale(yawScale, 1);

    // Asymmetric radial gradient: inner circle is a zero-radius point AT
    // the apex (peak colour there); outer circle is offset by drapeOffset
    // along +X (toward the nose) with the full radius. Canvas interpolates
    // along the path from inner to outer centre, producing a teardrop
    // blush shape — fades longer and softer toward the face centre, and
    // terminates tighter on the outer cheek side. This is how real blush
    // "si sfuma verso il naso" on a real face; the previous symmetric
    // radial stopped the same distance in every direction and read as a
    // circular patch rather than diffuse pigment.
    //
    // With drapeOffset = 0.30 * radius:
    //   - nose-side reach: radius + drapeOffset = 1.30 × radius
    //   - outer-side reach: radius − drapeOffset = 0.70 × radius
    const drapeOffset = radius * 0.24 * (options.drapeScale ?? 1);
    const grad = octx.createRadialGradient(
      0, 0, 0,
      drapeOffset, 0, radius
    );
    // v0.4.4: flatter falloff so colour holds out farther from the apex
    // and reads as cheek coverage rather than a point-source tint. Peak
    // opacity now survives to 40% of radius (was 30%) and ~60% at mid.
    grad.addColorStop(0.00, `rgba(${r}, ${g}, ${b}, ${alpha})`);
    grad.addColorStop(0.32, `rgba(${r}, ${g}, ${b}, ${alpha * 0.72})`);
    grad.addColorStop(0.58, `rgba(${r}, ${g}, ${b}, ${alpha * 0.38})`);
    grad.addColorStop(0.82, `rgba(${r}, ${g}, ${b}, ${alpha * 0.14})`);
    grad.addColorStop(1.00, `rgba(${r}, ${g}, ${b}, 0)`);
    octx.fillStyle = grad;
    // Fill a rect wide enough to contain the full gradient extent; outside
    // the outer circle the gradient clamps to its last stop (alpha 0), so
    // the over-drawn area stays transparent.
    const cover = radius + drapeOffset + 4;
    octx.fillRect(-cover, -cover, cover * 2, cover * 2);
    octx.restore();
  }

  // Constrain blush to the semantic cheek/skin matte when available; this
  // keeps blush off lips, eyes, brows, hair and background while preserving
  // the soft radial falloff. Landmark face-oval clipping remains as fallback.
  const semanticMatte = semanticCheekMatte(options.semanticMap, W, H);
  if (semanticMatte) {
    octx.globalCompositeOperation = 'destination-in';
    octx.drawImage(semanticMatte, 0, 0);
    octx.globalCompositeOperation = 'source-over';
  } else {
    const faceMask = document.createElement('canvas');
    faceMask.width = W;
    faceMask.height = H;
    const mctx = faceMask.getContext('2d', { willReadFrequently: true });
    if (mctx) {
    const featherPx = Math.max(2, Math.min(W, H) * 0.005);
    mctx.fillStyle = '#ffffff';
    mctx.beginPath();
    tracePath(mctx, detection, FACE_OVAL);
    mctx.fill();
    stackBlur(mctx, 0, 0, W, H, featherPx);
    octx.globalCompositeOperation = 'destination-in';
    octx.drawImage(faceMask, 0, 0);
    octx.globalCompositeOperation = 'source-over';
    }
  }

  ctx.drawImage(overlay, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  return { canvas: out };
}
